import { duration } from "@formant/core/model/duration";
import { Timestamp } from "@formant/core/model/Timestamp";
import { Uuid } from "@formant/core/model/Uuid";

import { IPingInfo } from "./models/IPingInfo";

export interface IPing {
    sent: Timestamp;
}

export interface IPingPong {
    sent: Timestamp;
    received: Timestamp;
}

export type PingValue = IPing | IPingPong;
export type PingMap = Map<Uuid, PingValue>;

const isResolved = (it: PingValue): it is IPingPong =>
    "received" in it && it.received !== undefined;
const isUnresolved = (it: PingValue): it is IPing =>
    !("received" in it) || it.received === undefined;

const windowSize = duration.second * 2;
const truncateCutoff = duration.second * 10;

interface IGatherPingV2MetricsOptions {
    temporalNow?: number;
}

export function gatherPingV2Metrics(
    pingMap: PingMap,
    options: IGatherPingV2MetricsOptions = {},
): IPingInfo | null {
    const pingValues = Array.from(pingMap.values());

    const pongs = pingValues.filter(isResolved);
    const hasPongs = pongs.length > 0;
    const latestTransmittedPing = Math.max(...pingValues.map(_ => _.sent));
    const latestReceivedPong = Math.max(...pongs.map(_ => _.received));

    let result: IPingInfo | null;
    if (hasPongs) {
        // if a ping was JUST sent, filter it out for the overall count.
        // without doing this, even reliable connections will always indicate
        // fewer pongs than pings (which gives a false sense of packet loss)
        const pingsSent =
            Date.now() - latestTransmittedPing <= duration.millisecond
                ? pingValues.length - 1
                : pingValues.length;
        const pongsReceived =
            pongs.length > pingsSent ? pingsSent : pongs.length;

        const pings = pongs.map(_ => _.received - _.sent);
        const { standardDeviation, mean: average, jitter } = getStats(pings);

        const oldPings = pingValues.filter(
            _ => _.sent < latestReceivedPong - windowSize,
        );
        const oldLostPings = oldPings.filter(isUnresolved);
        const loss =
            oldLostPings.length === 0
                ? 0
                : oldLostPings.length / oldPings.length;

        result = {
            pingsSent,
            pongsReceived,
            average,
            standardDeviation,
            jitter,
            max: Math.max(...pings),
            min: Math.min(...pings),
            loss,
        };
    } else {
        result = null;
    }

    const { temporalNow = Date.now() } = options;
    const truncateFrom = hasPongs ? latestReceivedPong : temporalNow;
    const truncateLimit = truncateFrom - truncateCutoff;
    Array.from(pingMap.entries()).forEach(_ => {
        const [key, value] = _;
        if (value.sent < truncateLimit) {
            pingMap.delete(key);
        }
    });

    return result;
}

function getStats(array: number[]): {
    mean: number;
    standardDeviation: number;
    jitter: number;
} {
    const arrayLength = array.length;
    if (arrayLength === 0) {
        return { mean: NaN, standardDeviation: -1, jitter: NaN };
    }

    const mean =
        array.reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0,
        ) / arrayLength;
    const standardDeviation = Math.sqrt(
        array
            .map(x => Math.pow(x - mean, 2))
            .reduce((accumulator, currentValue) => accumulator + currentValue) /
            arrayLength,
    );

    if (arrayLength === 1) {
        return { mean, standardDeviation, jitter: NaN };
    }

    let jitterSum = 0;
    for (let index = 1; index < arrayLength; index++) {
        jitterSum += Math.abs(array[index] - array[index - 1]);
    }

    const jitter = jitterSum / (arrayLength - 1);
    return { mean, standardDeviation, jitter };
}
